import type { S3Client } from "@aws-sdk/client-s3";
import {
  CopyObjectCommand,
  DeleteObjectCommand,
  HeadObjectCommand,
} from "@aws-sdk/client-s3";

import {
  FOR_APPROVAL_FOLDER_NAME,
  RESULTS_FOLDER_NAME,
} from "@/storage/folderNames";

/** The `results/` key a `for_approval/` candidate is promoted to. */
export function approvedResultKey(forApprovalKey: string): string {
  return forApprovalKey.replace(
    `/${FOR_APPROVAL_FOLDER_NAME}/`,
    `/${RESULTS_FOLDER_NAME}/`,
  );
}

// S3 `CopySource` is `bucket/key`; the key must be URI-encoded while keeping the
// `/` separators intact.
function encodeCopySource(bucket: string, key: string): string {
  const encodedKey = key.split("/").map(encodeURIComponent).join("/");
  return `${bucket}/${encodedKey}`;
}

/** Whether an object exists at `key`. Treats a 404 (NotFound) as "absent". */
async function objectExists(
  client: S3Client,
  bucket: string,
  key: string,
): Promise<boolean> {
  try {
    await client.send(new HeadObjectCommand({ Bucket: bucket, Key: key }));
    return true;
  } catch (error: unknown) {
    if (
      typeof error === "object" &&
      error !== null &&
      "$metadata" in error &&
      (error as { $metadata?: { httpStatusCode?: number } }).$metadata
        ?.httpStatusCode === 404
    ) {
      return false;
    }
    throw error;
  }
}

/**
 * Approve a candidate by moving it from `for_approval/` to `results/`.
 *
 * Copies the object to its `results/` key, then deletes the `for_approval/` source.
 * Requires the Quality Control role (write to `results/`, delete from `for_approval/`).
 */
export async function approveByMoving(
  client: S3Client,
  bucket: string,
  forApprovalKey: string,
): Promise<string> {
  const destinationKey = approvedResultKey(forApprovalKey);
  if (await objectExists(client, bucket, destinationKey)) {
    throw new Error(
      `A released result already exists at ${destinationKey}; refusing to overwrite it.`,
    );
  }
  await client.send(
    new CopyObjectCommand({
      Bucket: bucket,
      Key: destinationKey,
      CopySource: encodeCopySource(bucket, forApprovalKey),
    }),
  );
  await client.send(
    new DeleteObjectCommand({ Bucket: bucket, Key: forApprovalKey }),
  );
  return destinationKey;
}

export async function rejectByDeletion(
  client: S3Client,
  bucket: string,
  forApprovalKey: string,
): Promise<string> {
  await client.send(
    new DeleteObjectCommand({ Bucket: bucket, Key: forApprovalKey }),
  );
  return forApprovalKey;
}
